import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLazyLoad } from '../../../../hooks/use-lazy-load';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
  EXERCISE_FILTERS_PAGE_KEYS,
  ExerciseFiltersPageKey,
  IExerciseFilters,
  cloneExerciseFilters,
  loadExerciseFilters,
} from '../exercise-filters';
import { buildExerciseListLazyParams } from '../exercise-list/build-exercise-list-lazy-params';
import type { IExerciseListFilterQuery } from '../exercise-list/exercise-list-filter-query';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_PAGE_SIZE = 15;

function serializeExerciseListQuery(
  searchValue: string,
  filters: IExerciseFilters,
  language: string,
): string {
  return JSON.stringify({ language, searchValue, filters });
}

type ExerciseListTabKey = keyof typeof EXERCISE_FILTERS_PAGE_KEYS;

export function useExerciseListLazyTab(
  useLazyExerciseListQuery: Parameters<typeof useLazyLoad>[0],
  tabKey: ExerciseListTabKey,
) {
  const { i18n } = useTranslation();

  const filtersPageKey: ExerciseFiltersPageKey =
    EXERCISE_FILTERS_PAGE_KEYS[tabKey];

  const initialFilters = useMemo(
    () => loadExerciseFilters(filtersPageKey),
    [filtersPageKey],
  );

  const [queryState, setQueryState] = useState<IExerciseListFilterQuery>({
    searchValue: '',
    appliedFilters: initialFilters,
  });

  const lazyParams = useMemo(
    () => ({
      ...buildExerciseListLazyParams(queryState, DEFAULT_PAGE_SIZE),
    }),
    [queryState],
  );

  const {
    items: exercises,
    footerRef,
    resetAll,
    refetchAll,
    isFetching,
    error,
  } = useLazyLoad(useLazyExerciseListQuery, lazyParams);

  const firstFetchStartedRef = useRef(false);
  const [isFirstFetchSettled, setIsFirstFetchSettled] = useState(false);

  useEffect(() => {
    if (isFetching) {
      firstFetchStartedRef.current = true;
      return;
    }
    if (isFirstFetchSettled || !firstFetchStartedRef.current) {
      return;
    }
    setIsFirstFetchSettled(true);
  }, [isFetching, isFirstFetchSettled]);

  const exercisesRef = useRef(exercises);
  const isFetchingRef = useRef(isFetching);
  const errorRef = useRef(error);
  exercisesRef.current = exercises;
  isFetchingRef.current = isFetching;
  errorRef.current = error;

  const initialRecoveryTriggeredRef = useRef(false);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (initialRecoveryTriggeredRef.current) {
        return;
      }

      const currentExercises = exercisesRef.current;
      const noDataYet =
        currentExercises === null || (currentExercises?.length ?? 0) === 0;
      if (!noDataYet || isFetchingRef.current || errorRef.current) {
        return;
      }

      initialRecoveryTriggeredRef.current = true;
      void resetAll(true);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [resetAll]);

  const resetDebounced = useCallback(() => {
    resetAll(true);
  }, [resetAll]);

  const [debouncedOnQueryChange] = useDebounce(
    resetDebounced,
    SEARCH_DEBOUNCE_MS,
  );

  const prevSerializedQueryRef = useRef<string | null>(null);

  useEffect(() => {
    const key = serializeExerciseListQuery(
      queryState.searchValue,
      queryState.appliedFilters,
      i18n.language,
    );
    if (prevSerializedQueryRef.current === null) {
      prevSerializedQueryRef.current = key;
      return;
    }
    if (prevSerializedQueryRef.current === key) {
      return;
    }
    prevSerializedQueryRef.current = key;
    debouncedOnQueryChange();
  }, [queryState, i18n.language, debouncedOnQueryChange]);

  const handleQueryChange = useCallback((next: IExerciseListFilterQuery) => {
    setQueryState((prev) => {
      if (
        prev.searchValue === next.searchValue &&
        JSON.stringify(prev.appliedFilters) ===
          JSON.stringify(next.appliedFilters)
      ) {
        return prev;
      }
      return {
        searchValue: next.searchValue,
        appliedFilters: cloneExerciseFilters(next.appliedFilters),
      };
    });
  }, []);

  const refetch = useCallback(() => {
    void resetAll(true);
  }, [resetAll]);

  const handleRetry = useCallback(() => {
    void resetAll(true);
  }, [resetAll]);

  const isLoading =
    !isFirstFetchSettled || Boolean(isFetching && !exercises?.length);

  return {
    exercises: exercises ?? null,
    isLoading,
    error,
    hasData: Boolean(exercises?.length),
    refetch,
    refetchAll,
    footerRef,
    handleQueryChange,
    handleRetry,
    isFetching,
  };
}
